import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const IMG_HERO = (process.env.PUBLIC_URL || '') + '/hero-top.svg';
const IMG_ABOUT_IMG = (process.env.PUBLIC_URL || '') + '/about-bg.svg';
const IMG_CTA_BG = (process.env.PUBLIC_URL || '') + '/hero-bottom.svg';
const ICON_INSTAGRAM = (process.env.PUBLIC_URL || '') + '/icons/instagram.svg';
const ICON_FACEBOOK  = (process.env.PUBLIC_URL || '') + '/icons/facebook.svg';
const ICON_YOUTUBE   = (process.env.PUBLIC_URL || '') + '/icons/youtube.svg';
const ICON_TIKTOK    = (process.env.PUBLIC_URL || '') + '/icons/tiktok.svg';

const Page = styled.div`
  max-width: 1100px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Section = styled.section`
  margin: 64px 0;
`;

const H2 = styled.h2`
  font-size: 2rem;
  margin-bottom: 10px;
  color: var(--accent);
`;

const Lead = styled.p`
  font-size: 1.1rem;
  color: var(--muted);
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
  flex-wrap: wrap;
`;

const Cta = styled(Link)`
  text-decoration: none;
  padding: 12px 18px;
  border-radius: 24px;
  border: none;
  background: var(--accent);
  color: var(--accent-contrast);
  font-weight: 700;
`;

const Media = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--surface);
`;

const Img = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 10px;
  border: 1px solid var(--border);
`;

/* ---------- Hero (centered over background) ---------- */
const HeroWrap = styled.section`
  margin: 0;
  padding: 120px 20px;
  width: 100vw;
  min-height: 60vh;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  border-radius: 0;
  background: center/cover no-repeat url(${IMG_HERO});
  display: grid;
  place-items: center;
  text-align: center;
  color: #fff;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 80px 16px;
    min-height: 70vh;
  }
`;

const HeroCard = styled.div`
  max-width: 820px;
  width: 100%;
  backdrop-filter: blur(2px);
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  line-height: 1.1;
  margin: 0 0 12px;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0,0,0,0.35);
  @media (max-width: 600px) { font-size: 2.1rem; }
`;

const HeroLead = styled.p`
  font-size: 1.15rem;
  margin: 0;
  color: #f1f1f1;
  @media (max-width: 600px) { font-size: 1rem; }
`;

const HeroButtons = styled(Buttons)`
  justify-content: center;
`;

/* ---------- Full-Bleed Split (About) ---------- */
const SplitWrap = styled.section`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  @media (max-width: 900px) { grid-template-columns: 1fr; min-height: auto; }
`;

const SplitImage = styled.div`
  background: center/cover no-repeat url(${IMG_ABOUT_IMG});
  min-height: 50vh;
`;

const SplitContent = styled.div`
  display: flex;
  align-items: center;
  padding: 40px 8vw;
  background: var(--bg);
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
`;

const SocialIcon = styled.img`
  width: 28px;
  height: 28px;
  display: block;
`;

/* ---------- Full-Bleed Featured Grid ---------- */
const FeaturedWrap = styled.section`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding: 48px 3vw;
`;

const FeaturedGrid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const Home = () => {
  const { addItem } = useCart();
  const [addedMap, setAddedMap] = useState({});
  const [featured, setFeatured] = useState([]);
  const [loadingFeat, setLoadingFeat] = useState(true);
  const [errorFeat, setErrorFeat] = useState('');

  const heroTopRef = useRef(null);
  const heroBottomRef = useRef(null);
  const gridRef = useRef(null);
  const splitRef = useRef(null);
  const featWrapRef = useRef(null);
  const featHeaderRef = useRef(null);

  const onQuickAdd = (p) => {
    addItem(p.name, p.price);
    setAddedMap((m) => ({ ...m, [p.id]: true }));
    setTimeout(() => setAddedMap((m) => ({ ...m, [p.id]: false })), 900);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/products?featured=true&limit=6');
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to load featured');
        if (!cancelled) setFeatured(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setErrorFeat(e.message || 'Failed to load featured');
      } finally {
        if (!cancelled) setLoadingFeat(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Entrance animations
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (heroTopRef.current) {
        gsap.from(heroTopRef.current.children, { y: 16, autoAlpha: 0, duration: 1, ease: 'power2.out', stagger: 0.08 });
      }
    });
    return () => ctx.revert();
  }, []);

  // Scroll-based reveals
  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      if (splitRef.current) {
        gsap.from(splitRef.current.children, {
          y: 14,
          autoAlpha: 0,
          duration: 1,
          ease: 'power2.out',
          stagger: 0.06,
          scrollTrigger: {
            trigger: splitRef.current,
            start: 'top 80%',
          },
        });
      }
      if (featHeaderRef.current) {
        gsap.from(featHeaderRef.current, {
          y: 10,
          autoAlpha: 0,
          duration: 1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: featWrapRef.current || featHeaderRef.current,
            start: 'top 85%',
          },
        });
      }
      if (heroBottomRef.current) {
        gsap.from(heroBottomRef.current.children, {
          y: 16,
          autoAlpha: 0,
          duration: 1,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: {
            trigger: heroBottomRef.current,
            start: 'top 85%',
          },
        });
      }
    });
    return () => ctx.revert();
  }, []);

  // Animate featured grid after data loads
  useLayoutEffect(() => {
    if (!featured?.length || !gridRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(gridRef.current.children, {
        y: 18,
        autoAlpha: 0,
        duration: 1,
        ease: 'power2.out',
        stagger: 0.07,
        scrollTrigger: {
          trigger: featWrapRef.current || gridRef.current,
          start: 'top 80%',
        },
      });
    }, gridRef);
    return () => ctx.revert();
  }, [featured]);

  return (
    <Page>
      {/* Top hero */}
      <HeroWrap>
        <HeroCard ref={heroTopRef}>
          <HeroTitle>Lamar's Clothes</HeroTitle>
          <HeroLead>
            Fresh styles for every day. Discover trending fits, everyday essentials,
            and quality pieces designed for comfort and confidence.
          </HeroLead>
          <HeroButtons>
            <Cta to="/products">Shop New Arrivals</Cta>
          </HeroButtons>
        </HeroCard>
      </HeroWrap>

      {/* About Us full-screen split */}
      <SplitWrap>
        <SplitImage />
        <SplitContent>
          <div ref={splitRef}>
            <H2>About Us</H2>
            <Lead>
              Lamar's Clothes brings together everyday comfort and clean design. We focus on
              quality fabrics, versatile fits, and fair pricing-so building a wardrobe you love is simple.
            </Lead>
            <SocialRow>
              <a href="https://instagram.com/lamarsclothes" target="_blank" rel="noreferrer" aria-label="Instagram">
                <SocialIcon src={ICON_INSTAGRAM} alt="Instagram" />
              </a>
              <a href="https://facebook.com/lamarsclothes" target="_blank" rel="noreferrer" aria-label="Facebook">
                <SocialIcon src={ICON_FACEBOOK} alt="Facebook" />
              </a>
              <a href="https://youtube.com/@lamarsclothes" target="_blank" rel="noreferrer" aria-label="YouTube">
                <SocialIcon src={ICON_YOUTUBE} alt="YouTube" />
              </a>
              <a href="https://tiktok.com/@lamarsclothes" target="_blank" rel="noreferrer" aria-label="TikTok">
                <SocialIcon src={ICON_TIKTOK} alt="TikTok" />
              </a>
            </SocialRow>
          </div>
        </SplitContent>
      </SplitWrap>

      {/* Featured Products full-width */}
      <FeaturedWrap ref={featWrapRef}>
        <H2 ref={featHeaderRef}>Featured</H2>
        <div style={{ height: 12 }} />
        {loadingFeat && <div style={{ color: 'var(--muted)', marginBottom: 12 }}>Loading…</div>}
        {errorFeat && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{errorFeat}</div>}
        <FeaturedGrid ref={gridRef}>
          {featured.map((p) => (
            <div key={p.id} style={{
              border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden'
            }}>
              <img src={p.image} alt={p.name} style={{ width: '100%', height: 200, objectFit: 'contain', background: 'var(--bg)' }} />
              <div style={{ padding: '12px 14px' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{p.name}</div>
                <div style={{ color: 'var(--accent)', fontWeight: 700 }}>
                  {typeof p.price === 'number' ? `$${p.price.toFixed(2)}` : ''}
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Cta to={`/products/${p.slug}`}>View</Cta>
                  {typeof p.price === 'number' ? (
                    <button
                      onClick={() => onQuickAdd(p)}
                      style={{ padding: '10px 14px', borderRadius: 20, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer' }}
                    >
                      {addedMap[p.id] ? 'Added!' : 'Add to Cart'}
                    </button>
                  ) : (
                    <Link to={`/products/${p.slug}`} style={{ color: 'var(--accent)', fontWeight: 700 }}>Contact for pricing</Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </FeaturedGrid>
        {!loadingFeat && !errorFeat && featured.length === 0 ? (
          <div style={{ color: 'var(--muted)', marginTop: 12 }}>No featured products yet.</div>
        ) : null}
      </FeaturedWrap>

      {/* Bottom hero CTA */}
      <HeroWrap style={{ backgroundImage: `url(${IMG_CTA_BG})` }}>
        <HeroCard ref={heroBottomRef}>
          <HeroTitle style={{ marginBottom: 16 }}>Find Your Next Favorite</HeroTitle>
          <HeroButtons>
            <Cta to="/products">Shop Now</Cta>
          </HeroButtons>
        </HeroCard>
      </HeroWrap>
    </Page>
  );
};

export default Home;
